/**
 * Разбор ответа `/dashboard/accounts`.
 *
 * Тот же payload приходит двумя путями — HTTP-запросом при загрузке и push'ем
 * по general-socket, когда сделка или перевод изменили балансы на другом
 * устройстве. Раньше обработка была написана дважды и разошлась: сокетный путь
 * не фильтровал счета по саб-аккаунту и подменял кэш чужими строками. Поэтому
 * логика живёт здесь одна и вызывается из обоих мест.
 *
 * Вынесено отдельным модулем без зависимостей от стора и API — так это можно
 * покрыть тестами, не поднимая sircapApi и Capacitor.
 */

interface WithSubAccount {
  sub_account?: string | null;
}

interface WithCurrencyAmount {
  currency?: { code?: string } | null;
  amount?: number | null;
}

/** Депозитные счета (кэш) и брокерские (портфель) в порядке ответа сервера. */
export interface AccountsPayload<D = any, B = any> {
  depositAccounts: D[];
  brokerageAccounts: B[];
}

/**
 * Сервер отдаёт кортеж [депозитные, брокерские]. Приводим к объекту и защищаем
 * от неполного ответа: раньше сокетный обработчик брал accounts[0] вслепую, а
 * accounts[1] вообще игнорировал.
 */
export function normalizeAccountsPayload<D = any, B = any>(
  raw: unknown,
): AccountsPayload<D, B> {
  const tuple = Array.isArray(raw) ? raw : [];

  return {
    depositAccounts: Array.isArray(tuple[0]) ? (tuple[0] as D[]) : [],
    brokerageAccounts: Array.isArray(tuple[1]) ? (tuple[1] as B[]) : [],
  };
}

/**
 * Оставляет счета выбранного саб-аккаунта, а без выбора — только собственные.
 * Именно этой фильтрации не хватало на сокетном пути.
 */
export function filterBySubAccount<T extends WithSubAccount>(
  accounts: T[],
  selectedSubAccount: string | null,
): T[] {
  return accounts.filter((account) =>
    selectedSubAccount
      ? account.sub_account === selectedSubAccount
      : !account.sub_account,
  );
}

/**
 * Валюта показывается, если по ней есть деньги; AMD видна всегда. Ранее
 * пересчитывалось только при HTTP-загрузке, поэтому после push'а по сокету
 * новая валюта на балансе оставалась скрытой.
 */
export function mergeShowConfig(
  current: Record<string, boolean>,
  depositAccounts: WithCurrencyAmount[],
): Record<string, boolean> {
  if (!depositAccounts.length) return current;

  const fromAccounts = Object.fromEntries(
    depositAccounts
      .filter((account) => !!account.currency?.code)
      .map((account) => [
        account.currency!.code as string,
        account.currency!.code === "AMD" || !!account.amount,
      ]),
  );

  return { ...current, ...fromAccounts };
}
